import type {
  ComposerFileAttachment,
  ComposerImageAttachment,
  PersistedComposerFileAttachment,
  PersistedComposerImageAttachment,
} from "./composerDraftStore";
import type { BrowserFileAttachmentStore } from "./composerFileAttachmentStore";

export function revokeObjectPreviewUrl(previewUrl: string): void {
  if (typeof URL !== "undefined" && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
}

export function normalizePersistedAttachment(
  value: unknown,
): PersistedComposerImageAttachment | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  const { id, name, mimeType, sizeBytes, dataUrl } = candidate;
  if (
    typeof id !== "string" ||
    typeof name !== "string" ||
    typeof mimeType !== "string" ||
    typeof sizeBytes !== "number" ||
    !Number.isFinite(sizeBytes) ||
    typeof dataUrl !== "string" ||
    id.length === 0 ||
    dataUrl.length === 0
  )
    return null;
  return { id, name, mimeType, sizeBytes, dataUrl };
}

export function normalizePersistedFileAttachment(
  value: unknown,
): PersistedComposerFileAttachment | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  const { id, name, mimeType, sizeBytes } = candidate;
  if (
    typeof id !== "string" ||
    id.length === 0 ||
    typeof name !== "string" ||
    typeof mimeType !== "string" ||
    typeof sizeBytes !== "number" ||
    !Number.isSafeInteger(sizeBytes) ||
    sizeBytes < 0
  ) {
    return null;
  }
  return { id, name, mimeType, sizeBytes };
}

export async function hydrateFilesFromPersisted(
  attachments: ReadonlyArray<PersistedComposerFileAttachment>,
  store: Pick<BrowserFileAttachmentStore, "read">,
): Promise<ComposerFileAttachment[] | null> {
  const files = await Promise.all(
    attachments.map(async (attachment) => {
      const file = await store.read(attachment);
      return { ...attachment, type: "file" as const, file };
    }),
  );
  return files.length === attachments.length ? files : null;
}

function hydratePersistedComposerImageAttachment(
  attachment: PersistedComposerImageAttachment,
): File | null {
  const decoded = decodeDataUrl(attachment.dataUrl, attachment.mimeType);
  if (!decoded) return null;
  return new File([decoded.content], attachment.name, { type: decoded.mimeType });
}

function decodeDataUrl(
  dataUrl: string,
  fallbackMimeType: string,
): { readonly content: string | Uint8Array<ArrayBuffer>; readonly mimeType: string } | null {
  const commaIndex = dataUrl.indexOf(",");
  const header = commaIndex === -1 ? dataUrl : dataUrl.slice(0, commaIndex);
  const payload = commaIndex === -1 ? "" : dataUrl.slice(commaIndex + 1);
  if (payload.length === 0) return null;
  try {
    return header.includes(";base64")
      ? { content: decodeBase64Payload(payload), mimeType: fallbackMimeType }
      : {
          content: decodeTextPayload(payload),
          mimeType: dataUrlMimeType(header, fallbackMimeType),
        };
  } catch {
    return null;
  }
}

function dataUrlMimeType(header: string, fallbackMimeType: string): string {
  if (!header.startsWith("data:") || !header.includes(";")) return fallbackMimeType;
  return header.slice("data:".length, header.indexOf(";")) || fallbackMimeType;
}

function decodeTextPayload(payload: string): string {
  return decodeURIComponent(payload);
}

function decodeBase64Payload(payload: string): Uint8Array<ArrayBuffer> {
  const binary = atob(payload);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

export function hydrateImagesFromPersisted(
  attachments: ReadonlyArray<PersistedComposerImageAttachment>,
): ComposerImageAttachment[] {
  return attachments.flatMap((attachment) => {
    const file = hydratePersistedComposerImageAttachment(attachment);
    return file
      ? [
          {
            type: "image" as const,
            id: attachment.id,
            name: attachment.name,
            mimeType: attachment.mimeType,
            sizeBytes: attachment.sizeBytes,
            previewUrl: attachment.dataUrl,
            file,
          },
        ]
      : [];
  });
}
